import { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  useToast,
  Text,
  FormLabel,
  FormControl,
  InputGroup,
  InputLeftAddon,
} from "@chakra-ui/react";
import { BrandButton } from "../BrandButton/BrandButton";
import { getAxiosClient } from "../../services/HttpsService";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/services/state/store";
import { useParams } from "react-router-dom";
import { fetchHttpIntegrationsByApplication } from "../../services/state/http-integration/httpIntegration-async-calls";
import { DropDown } from "../Select/DropDown";
import { ConnectedSystem } from "@/services/state/connected-systems/connectedSystem-interface";

export const AddHttpIntegrationModal = ({
  isOpen,
  onClose,
  edit = false,
  tenant,
}: {
  isOpen: boolean;
  onClose: () => void;
  edit?: boolean;
  tenant?: any;
}) => {
  const initialRef = useRef<HTMLInputElement | null>(null);
  const toast = useToast();
  const dispatch = useDispatch<AppDispatch>();
  const { applicationId } = useParams();

  const connectedSystems = useSelector(
    (state: RootState) => state.connectedSystems.connectedSystems
  );

  const [name, setName] = useState("");
  const [selectedConnectedSystem, setSelectedConnectedSystem] =
    useState<ConnectedSystem>();
  const [loading, setLoading] = useState(false); // New loading state

  const selectedApplication = useSelector(
    (state: RootState) => state.applications.selectedApplication
  );

  useEffect(() => {
    setSelectedConnectedSystem(connectedSystems.at(0));
  }, [connectedSystems]);

  const onCreate = async () => {
    if (name !== "") {
      const url = "/api/http-integration";
      const data = {
        intgNm: selectedApplication?.appPrefix
          ? selectedApplication.appPrefix + "_" + name
          : name,
        appUuid: applicationId,
        csUuid: selectedConnectedSystem?.uuid,
      };

      setLoading(true); // Start loading
      try {
        const response = await getAxiosClient().post(url, data);

        if (response.status === 201) {
          toast({
            title: "Successfully created new integration",
            description: "Created new integration with entered details",
            position: "top",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          onClose();
          dispatch(fetchHttpIntegrationsByApplication(applicationId as string));
          setName("");
        } else {
          toast({
            title: "Some error has occurred",
            description: "Please try again",
            position: "top",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error creating tenant:", error);
        toast({
          title: "Error",
          description: "An error occurred while creating new integration.",
          position: "top",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false); // Stop loading
      }
    } else {
      toast({
        title: "Name is required",
        description: "Please enter a name and try again",
        position: "top",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const onUpdate = async () => {
    if (name !== "") {
      const url = `/tenant/${tenant?.id}`;
      const data = {
        isActive: true,
      };
      setLoading(true); // Start loading
      try {
        const response = await getAxiosClient().put(url, data);

        if (response.status === 202) {
          toast({
            title: "Successfully updated integration",
            description: "Updated integration with entered details",
            position: "top",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          onClose();
          dispatch(fetchHttpIntegrationsByApplication(applicationId as string));
          setName("");
        } else {
          toast({
            title: "Some error has occurred",
            description: "Please try again",
            position: "top",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error updating integration:", error);
        toast({
          title: "Error",
          description: "An error occurred while updating new integration.",
          position: "top",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false); // Stop loading
      }
    } else {
      toast({
        title: "Name is required",
        description: "Please enter a name and try again",
        position: "top",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      initialFocusRef={initialRef}
      size="xl"
      isCentered
    >
      <ModalOverlay
        zIndex={1}
        backdropFilter="blur(10px)" // ← This will blur the page behind the modal
        bg="rgba(0, 0, 0, 0.4)"
      />
      <ModalContent
        bg="rgba(0, 0, 0, 0.9)"
        backdropFilter="blur(20px)"
        border="1px solid rgba(255, 255, 255, 0.4)"
        borderRadius="20px"
        color="white"
      >
        <ModalCloseButton color="white" />
        <ModalBody>
          {edit ? (
            <Text fontSize="20px" fontWeight="semibold" my={4} color="white">
              Edit Integration
            </Text>
          ) : (
            <Text fontSize="20px" fontWeight="semibold" my={4} color="white">
              Create New Integration
            </Text>
          )}{" "}
          <FormControl isRequired>
            <FormLabel color="white">Name</FormLabel>
            <InputGroup>
              {selectedApplication?.appPrefix && (
                <InputLeftAddon
                  bg="rgba(255, 255, 255, 0.1)"
                  borderColor="rgba(255, 255, 255, 0.2)"
                  color="white"
                >
                  {selectedApplication.appPrefix}_
                </InputLeftAddon>
              )}
              <Input
                placeholder="Enter name of the integration"
                focusBorderColor="brand.500"
                ref={initialRef}
                fontSize="18px"
                value={name}
                onChange={(e) => setName(e.target.value)}
                overflowX="auto"
                isRequired
                disabled={edit}
                bg={
                  edit
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(255, 255, 255, 0.1)"
                }
                borderColor="rgba(255, 255, 255, 0.2)"
                color="white"
                _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
                _focus={{
                  borderColor: "brand.500",
                  boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.5)",
                }}
                _disabled={{
                  bg: "rgba(255, 255, 255, 0.05)",
                  color: "rgba(255, 255, 255, 0.5)",
                }}
              />
            </InputGroup>
          </FormControl>
          <FormControl id="cs" mt={4}>
            <FormLabel color="white">Connected System</FormLabel>
            <DropDown
              selected={{
                id: selectedConnectedSystem?.uuid as string,
                name: selectedConnectedSystem?.csNm as string,
                component: (
                  <Text>{selectedConnectedSystem?.csNm as string}</Text>
                ),
              }}
              title={<Text></Text>}
              options={connectedSystems.map((cs) => ({
                id: cs.uuid,
                name: cs.csNm,
                component: <Text>{cs.csNm}</Text>,
              }))}
              onOptionChange={(option) => {
                if (!Array.isArray(option)) {
                  setSelectedConnectedSystem(
                    connectedSystems.find((cs) => cs.uuid === option.id)
                  );
                }
              }}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          {edit ? (
            <BrandButton onClick={onUpdate} isLoading={loading}>
              Save Changes
            </BrandButton>
          ) : (
            <BrandButton onClick={onCreate} isLoading={loading}>
              Create
            </BrandButton>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
